import { uniqueBy } from './presentation';

const normalizedGenres = (item) =>
  (item.albumGenres || []).map((genre) => genre.trim().toLowerCase()).filter(Boolean);
const parseDate = (value) => {
  if (!value) return null;
  const normalized = value.trim().replace(/\./g, '-').replace(/-+$/, '');
  const date = new Date(normalized.includes('T') ? normalized : `${normalized}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
};
const ageInDays = (value, now) => {
  const date = parseDate(value);
  if (!date) return null;
  return Math.floor(
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() -
      new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()) /
      86400000,
  );
};
const reviewAlbum = (item, key) => ({
  id: item.albumId,
  slug: item.albumSlug,
  title: item.albumTitle,
  imageUrl: item.albumImageUrl,
  artist: item.artist,
  contentRating: item.albumContentRating,
  homeReasonKey: `home.discovery.${key}`,
  fallback: true,
});

// Build60 HomeScreen: a successful (even empty) server response is authoritative.
// It already handles rated/excluded editions and grounded personalization.
export function homeServerCandidates(data) {
  return data == null
    ? undefined
    : { ...data, albums: uniqueBy(data.albums || [], 'id').slice(0, 36) };
}

// The member error branch renders Retry, so its saved/followed legacy buckets are
// deliberately not used. TODAY_ALBUM_ENABLED=false also disables history writes
// and the separate anniversary selection in the submitted build.
export function homeGuestCandidates(home = {}, releases = [], now = new Date()) {
  const reviews = uniqueBy(
    ['recommendedReviews', 'koreanMusicReviews', 'globalMusicReviews', 'latestReviews'].flatMap(
      (key) => home[key] || [],
    ),
    'albumId',
  );
  const genreCounts = new Map();
  reviews.forEach((item) =>
    normalizedGenres(item).forEach((genre) =>
      genreCounts.set(genre, (genreCounts.get(genre) || 0) + 1),
    ),
  );
  const leadingGenre = [...genreCounts].sort((a, b) => b[1] - a[1])[0]?.[0];
  const buckets = [
    reviews
      .filter(
        (item) =>
          Number(item.albumReviewCount) >= 2 &&
          (!leadingGenre || normalizedGenres(item).includes(leadingGenre)),
      )
      .sort(
        (a, b) =>
          Number(b.albumAvgRating) - Number(a.albumAvgRating) ||
          Number(b.albumReviewCount) - Number(a.albumReviewCount),
      )
      .map((item) => reviewAlbum(item, 'genreRating')),
    [...(home.latestReviews || [])]
      .filter(
        (item) =>
          Number(item.albumReviewCount) >= 3 && (ageInDays(item.albumReleaseDate, now) ?? 0) >= 365,
      )
      .sort(
        (a, b) =>
          Number(b.albumReviewCount) - Number(a.albumReviewCount) ||
          (parseDate(b.createdAt)?.getTime() || 0) - (parseDate(a.createdAt)?.getTime() || 0),
      )
      .map((item) => reviewAlbum(item, 'steadyReviews')),
    releases
      .filter((item) => {
        const age = ageInDays(item.releaseDate, now);
        return age !== null && age >= 0 && age <= 31;
      })
      .map((item) => ({ ...item, homeReasonKey: 'home.discovery.monthlyRelease', fallback: true })),
    (home.recommendedReviews || []).map((item) => reviewAlbum(item, 'postpop')),
  ];
  const selected = [],
    used = new Set();
  let monthlyCount = 0;
  let added = true;
  while (selected.length < 12 && added) {
    added = false;
    for (const bucket of buckets) {
      if (selected.length >= 12) break;
      const monthlyLimitReached = monthlyCount >= 3;
      const index = bucket.findIndex(
        (item) =>
          !used.has(item.id) &&
          item.homeReasonKey !== selected[selected.length - 1]?.homeReasonKey &&
          (item.homeReasonKey !== 'home.discovery.monthlyRelease' || !monthlyLimitReached),
      );
      if (index < 0) continue;
      const [item] = bucket.splice(index, 1);
      selected.push(item);
      used.add(item.id);
      if (item.homeReasonKey === 'home.discovery.monthlyRelease') monthlyCount++;
      added = true;
    }
  }
  return { albums: selected, tracks: [] };
}
